import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { SkillData } from './SkillRadarChart'; // Réutilisation du type SkillData

interface SkillBarChartProps {
  skills: SkillData[];
  maxValue?: number;
  animationDuration?: number;
  colors?: {
    primary: string;
    secondary: string;
    text: string;
  };
  title?: string;
  id: string;
  showLegend?: boolean;
  filter?: (skill: SkillData) => boolean;
}

// Styles
const ChartContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ChartTitle = styled.h3`
  text-align: center;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.2rem;
`;

const SkillsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem;
`;

const SkillItem = styled(motion.div)`
  padding-bottom: 0.2rem;
`;

// Conteneur global qui sera cliquable
const SkillItemContent = styled.div`
  cursor: pointer;
`;

const SkillHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.3rem;
  align-items: flex-end;
`;

const SkillName = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  white-space: normal;
  line-height: 1.2;
`;

// Conteneur pour la jauge ET le bouton +/- 
const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressBarContainer = styled.div`
  flex: 1;
  height: 10px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  overflow: hidden;
  position: relative;
`;

const ProgressBar = styled(motion.div)<{ color: string }>`
  height: 100%;
  background: linear-gradient(90deg, ${props => props.color}80, ${props => props.color});
  border-radius: 5px;
  width: 0%;
`;

// Déplacement de ce composant en haut de la hiérarchie pour meilleure lisibilité
const ToggleButton = styled.button<{ isOpen: boolean; color: string }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background-color: ${({ theme }) => `${theme.colors.background}dd`};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  
  &:hover {
    background-color: ${props => props.color};
    color: white;
    border-color: ${props => props.color};
  }
  
  &:focus {
    outline: none;
  }
`;

const SkillInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.2rem;
`;

const SkillLevel = styled.div<{ color: string }>`
  font-size: 0.75rem;
  color: ${props => props.color};
  font-weight: 600;
`;

const SkillDetails = styled(motion.div)`
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
  padding: 0.5rem;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  border-left: 3px solid ${props => props.color || 'rgba(255, 255, 255, 0.2)'};
`;

const Legend = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  padding: 0 0.5rem;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// Composant principal
const SkillBarChart: React.FC<SkillBarChartProps> = ({
  skills,
  maxValue = 100,
  animationDuration = 800,
  colors = {
    primary: '#3498db',
    secondary: '#2980b9',
    text: '#ffffff'
  },
  title,
  id,
  showLegend = false,
  filter
}) => {
  // @ts-ignore
  const { t } = useTranslation('skills');
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null);
  
  // Niveau de compétence selon la valeur
  const getSkillLevel = (value: number): string => {
    if (value < 20) return t('common:skillLevels.beginner');
    if (value < 40) return t('common:skillLevels.intermediate');
    if (value < 60) return t('common:skillLevels.advanced');
    if (value < 80) return t('common:skillLevels.confirmed');
    return t('common:skillLevels.expert');
  };
  
  // Filtrer les compétences si un filtre est fourni
  const displayedSkills = filter ? skills.filter(filter) : skills;
  
  // Trier les compétences par valeur décroissante
  const sortedSkills = [...displayedSkills].sort((a, b) => b.value - a.value);
  
  // Gestionnaire pour afficher/masquer les détails d'une compétence
  const toggleSkillDetails = (skillName: string) => {
    if (expandedSkill === skillName) {
      setExpandedSkill(null);
    } else {
      setExpandedSkill(skillName);
    }
  };

  // Empêcher la propagation des événements de clic du bouton vers le conteneur parent
  const handleButtonClick = (e: React.MouseEvent, skillName: string) => {
    e.stopPropagation();
    toggleSkillDetails(skillName);
  };

  return (
    <ChartContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {title && <ChartTitle>{title}</ChartTitle>}
      
      <SkillsList>
        {sortedSkills.map((skill, index) => (
          <SkillItem
            key={`${id}-${skill.name}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.3, 
              delay: index * 0.05 
            }}
          >
            <SkillItemContent onClick={() => toggleSkillDetails(skill.name)}>
              <SkillHeader>
                <SkillName>{skill.name}</SkillName>
                <SkillLevel color={colors.primary}>
                  {getSkillLevel(skill.value)}
                </SkillLevel>
              </SkillHeader>
              
              <ProgressContainer>
                <ProgressBarContainer>
                  <ProgressBar
                    color={colors.primary}
                    initial={{ width: 0 }}
                    animate={{ width: `${(skill.value / maxValue) * 100}%` }}
                    transition={{ duration: animationDuration / 1000, delay: index * 0.05 }}
                  />
                </ProgressBarContainer>
                
                {skill.description && (
                  <ToggleButton 
                    isOpen={expandedSkill === skill.name}
                    color={colors.primary}
                    onClick={(e) => handleButtonClick(e, skill.name)}
                  >
                    {expandedSkill === skill.name ? '-' : '+'}
                  </ToggleButton>
                )}
              </ProgressContainer>
            </SkillItemContent>
            
            <AnimatePresence>
              {expandedSkill === skill.name && skill.description && (
                <SkillDetails
                  color={colors.primary}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {skill.description}
                  {skill.category && (
                    <div style={{ marginTop: '0.3rem', fontSize: '0.75rem', opacity: 0.8 }}>
                      {skill.category}
                    </div>
                  )}
                </SkillDetails>
              )}
            </AnimatePresence>
          </SkillItem>
        ))}
      </SkillsList>
      
      {showLegend && (
        <Legend>
          <span>{t('common:skillLevels.beginner')}</span>
          <span>{t('common:skillLevels.intermediate')}</span>
          <span>{t('common:skillLevels.advanced')}</span>
          <span>{t('common:skillLevels.confirmed')}</span>
          <span>{t('common:skillLevels.expert')}</span>
        </Legend>
      )}
    </ChartContainer>
  );
};

export default SkillBarChart;